import Alert from '@mui/material/Alert';
import { BigNumber, utils } from 'ethers';
import React, { useState, useContext, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useAccount, useBlockNumber } from 'wagmi';
import { ContractContext } from '../../../context-providers/ContractsProvider';
import { ServicesContext } from '../../../context-providers/ServicesProvider';
import { contractAddresses, tokenDecimals } from '../../../helpers/config/contracts.config';
import { hardcodedErrorCodes } from '../../../helpers/error-message.helper';
import AmountInput from '../../AmountInput/AmountInput';
import NereusButton from '../../NereusButton/NereusButton';
import StatusIcon from '../../StatusIcon/StatusIcon';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import './NXUSD_USDC_LP_StakeDialog.scss';
import {earnBalanceStore} from "../../../stores/EarnBalanceStore";

const buttonTexts = {
    approveAndLock: 'Approve and Stake NXUSD-USDC LP',
    enterAmount: 'Enter Amount',
    insufficientFunds: 'Insufficient Funds',
    approving: 'Approving NXUSD-USDC LP',
    deposit: 'Stake',
    depositing: 'Staking',
    retryApproveAndLock: 'Retry Approve and Stake NXUSD-USDC LP',
    retryLock: 'Retry Stake NXUSD-USDC LP'
};

const NXUSD_USDC_LP_StakeDialog: React.FC = observer(() => {
    const { data: blockNumber } = useBlockNumber({ watch: true });
    const { address: userAddress } = useAccount();
    const { depositWithdrawalEarnService } =  useContext(ServicesContext);
    const [amount, setAmount] = useState<string>('0');
    const [error, setError] = useState<string | null>(null);
    const [btnDisabled, setBtnDisabled] = useState<boolean>(true);
    const [approvedAmount, setApprovedAmount] = useState<BigNumber>(BigNumber.from('0'));
    const [buttonText, setButtonText] = useState<string>(buttonTexts.enterAmount);
    const [isPending, setIsPending] = useState<boolean>(false);
    const [isRetry, setIsRetry] = useState<boolean>(false);
    const { nrsContract, lpNxusdUsdcContract, lpNxusdUsdcStakingContract } = useContext(ContractContext);

    useEffect(() => {
        const getAndSetApprovedAmount = async () => {
            const allowed = await lpNxusdUsdcContract.read.allowance([userAddress, contractAddresses.base.lpNxusdUsdcStakingContract]);

            setApprovedAmount(BigNumber.from(allowed));
        }
        if (userAddress && blockNumber && lpNxusdUsdcContract) {
            getAndSetApprovedAmount();
        } else {
            setApprovedAmount(BigNumber.from('0'));
        }

    }, [userAddress, blockNumber, nrsContract]);

    // Fetch balances on mount to ensure data is fresh
    useEffect(() => {
        if (userAddress && lpNxusdUsdcContract && lpNxusdUsdcStakingContract) {
            earnBalanceStore.fetchBalancesNXUSD_USDC(nrsContract, lpNxusdUsdcContract, lpNxusdUsdcStakingContract, userAddress);
        }
    }, []);

    useEffect(() => {
        if (!amount || Number(amount) === 0) {
            return setButtonText(buttonTexts.enterAmount);
        } else {
            if (error) {
                if (error === hardcodedErrorCodes.FE_NOT_ENOUGH_WALLET_BALANCE) {
                    return setButtonText(buttonTexts.insufficientFunds);
                }
            }
            if (approvedAmount.gte(utils.parseUnits(amount, tokenDecimals.lp))) {
                if (isPending) {
                    return setButtonText(buttonTexts.depositing);
                } else {
                    return setButtonText(isRetry ? buttonTexts.retryLock : buttonTexts.deposit);
                }

            } else {
                if (isPending) {
                    return setButtonText(buttonTexts.approving);
                } else {
                    return setButtonText(isRetry ? buttonTexts.retryApproveAndLock : buttonTexts.approveAndLock);
                }
            }
        }

    }, [amount, error, approvedAmount, isRetry, isPending]);

    useEffect(() => {
        if ((amount && Number(amount) > 0) && (error !== hardcodedErrorCodes.FE_NOT_ENOUGH_WALLET_BALANCE)) {
            setBtnDisabled(false);
        } else {
            setBtnDisabled(true);
        }
    }, [amount, error]);

    useEffect(() => {
        if (amount && Number(amount) > Number(earnBalanceStore.lpNxusdUsdcTokenBalanceDecimal)) {
            setError(hardcodedErrorCodes.FE_NOT_ENOUGH_WALLET_BALANCE);
        } else {
            setError(null);
        }
    }, [amount, earnBalanceStore.lpNxusdUsdcTokenBalanceDecimal]);

    const handleApproveAndLock = async () => {
        setIsPending(true);
        setIsRetry(false);
        if (approvedAmount.lt(utils.parseUnits(amount, tokenDecimals.lp))) {
            try {
                setButtonText(buttonTexts.approving);
                await depositWithdrawalEarnService.approveAmountNXUSD_USDC_LP(utils.parseUnits(amount, tokenDecimals.lp));
                // Wait for approval to propagate on Base
                await new Promise(resolve => setTimeout(resolve, 3000));
            } catch (err) {
                setIsPending(false);
                setIsRetry(true);
                return;
            }
        }

        try {
            setButtonText(buttonTexts.depositing);
            await depositWithdrawalEarnService.depositNXUSD_USDC_LP(utils.parseUnits(amount, tokenDecimals.lp));
            if (userAddress) {
                earnBalanceStore.fetchBalancesNXUSD_USDC(nrsContract, lpNxusdUsdcContract, lpNxusdUsdcStakingContract, userAddress)
            }
        } catch (err) {
            setButtonText(buttonTexts.retryLock);
            setIsPending(false);
            setIsRetry(true);
            return;
        }
        setIsPending(false);
    };

    return (
        <ModalWrapper className="LockDialog"
            headerChildren={<h2 className="nereus-body-m">Lock</h2>}
            bodyChildren={<div className="LockDialog__body">
                <div>
                    <AmountInput label="Amount" asset="NXUSD-USDC LP" hasMaxButton={true} value={amount}
                                 available={earnBalanceStore.lpNxusdUsdcTokenBalanceDecimal}
                                 precision={18}
                                 onChange={amount => setAmount(amount + '')}/>
                    {error && <Alert className="LockDialog__error mt-4" variant="standard" severity="error">
                        <span className="nereus-caption">{error}</span>
                    </Alert>}
                </div>
                <div className="flex pb-4 mt-4">
                    <NereusButton disabled={btnDisabled || isPending} variant="contained"
                                  onClick={handleApproveAndLock} className="bg-light-green text-dark-blue w-full">
                        {buttonText} {isPending && <StatusIcon className="ml-1 inline" status="disabled-pending"/>}
                    </NereusButton>
                </div>
            </div>}
        />
    );
});

export default NXUSD_USDC_LP_StakeDialog;
